"use client";

import { useEffect } from "react";
import { clarityEvent, claritySetTag, clarityUpgrade, trackEvent } from "@/lib/analytics";
import {
  classifyRuntimeError,
  normalizeRuntimeErrorMessage,
  runtimeErrorEventName,
  isExpectedApiRejection,
  shouldAutoReloadForChunkError,
  reloadOnceForChunkError,
  type RuntimeErrorKind,
} from "@/lib/runtime-error-monitor";
import { reportClientError } from "@/lib/error-report";

function reportRuntimeError(kind: RuntimeErrorKind, error: unknown) {
  const message = normalizeRuntimeErrorMessage(error);
  claritySetTag("runtime_error_kind", kind);
  claritySetTag("runtime_error_message", message);
  claritySetTag("runtime_error_path", window.location.pathname);
  clarityEvent(runtimeErrorEventName(kind));
  clarityUpgrade(runtimeErrorEventName(kind));
  trackEvent("runtime_error", {
    error_kind: kind,
    error_message: message,
  });
}

export function RuntimeErrorMonitor() {
  useEffect(() => {
    const onError = (event: ErrorEvent) => {
      const error = event.error || event.message;
      const kind = classifyRuntimeError(error, "client_render");
      reportRuntimeError(kind, error);
      if (kind !== "chunk_load") {
        reportClientError({
          message: normalizeRuntimeErrorMessage(error),
          kind,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
      if (shouldAutoReloadForChunkError(error)) {
        reloadOnceForChunkError();
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (isExpectedApiRejection(error)) return;
      const kind = classifyRuntimeError(error, "unhandled_rejection");
      reportRuntimeError(kind, error);
      reportClientError({
        message: normalizeRuntimeErrorMessage(error),
        kind,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        stack: error instanceof Error ? error.stack : undefined,
      });
      if (shouldAutoReloadForChunkError(error)) {
        reloadOnceForChunkError();
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
